/**
 * Release Notes Dialog
 *
 * Displays the changelog in a dialog with markdown rendering.
 * Shows "What's New" content to users and tracks which version they've seen.
 */

import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Celebration as NewIcon,
} from "@mui/icons-material";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  useFetchChangelog,
  useMarkReleaseNotesSeen,
  getCurrentVersion,
} from "../../services/ReleaseNotesService";

interface ReleaseNotesDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Sanitizes URLs to prevent XSS attacks via javascript: or malicious data: URIs
 * while still allowing safe protocols, relative paths, and data:image URIs.
 */
const sanitizeUrl = (url: string): string => {
  const trimmedUrl = url.trim();

  // Allow relative paths (for local images)
  if (
    trimmedUrl.startsWith("/") ||
    trimmedUrl.startsWith("./") ||
    trimmedUrl.startsWith("../")
  ) {
    return trimmedUrl;
  }

  // Allow data:image URIs (safe for inline images)
  if (trimmedUrl.startsWith("data:image/")) {
    return trimmedUrl;
  }

  // Allow safe protocols
  const safeProtocols = ["http:", "https:", "mailto:"];
  try {
    const parsed = new URL(trimmedUrl);
    if (safeProtocols.includes(parsed.protocol)) {
      return trimmedUrl;
    }
  } catch {
    // If URL parsing fails, it might be a relative path without ./ prefix
    // Only allow if it doesn't contain a colon (which would indicate a protocol)
    if (!trimmedUrl.includes(":")) {
      return trimmedUrl;
    }
  }

  // Block everything else (javascript:, data:text/html, vbscript:, etc.)
  return "";
};

const ReleaseNotesDialog: React.FC<ReleaseNotesDialogProps> = ({
  open,
  onClose,
}) => {
  const { data: changelog, isLoading, isError } = useFetchChangelog();
  const markSeenMutation = useMarkReleaseNotesSeen();
  const currentVersion = getCurrentVersion();

  // Mark as seen when dialog opens
  useEffect(() => {
    if (open && currentVersion) {
      markSeenMutation.mutate(currentVersion);
    }
  }, [open, currentVersion]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NewIcon color="primary" />
          <Typography variant="h6" component="span">
            What's New
          </Typography>
          <Chip
            label={`v${currentVersion}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="warning">
            Unable to load release notes. Please try again later.
          </Alert>
        )}

        {changelog && (
          <Box
            sx={{
              "& h1": {
                fontSize: "1.75rem",
                fontWeight: 600,
                mt: 0,
                mb: 2,
                color: "primary.main",
              },
              "& h2": {
                fontSize: "1.25rem",
                fontWeight: 600,
                mt: 3,
                mb: 1.5,
                pb: 0.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& h3": {
                fontSize: "1rem",
                fontWeight: 600,
                mt: 2,
                mb: 1,
                color: "text.secondary",
              },
              "& p": {
                mb: 1.5,
                lineHeight: 1.6,
              },
              "& ul": {
                pl: 3,
                mb: 2,
              },
              "& li": {
                mb: 0.75,
                lineHeight: 1.5,
              },
              "& strong": {
                fontWeight: 600,
              },
              "& a": {
                color: "primary.main",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              },
              "& code": {
                backgroundColor: "action.hover",
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: "0.875em",
                fontFamily: "monospace",
              },
              "& img": {
                maxWidth: "100%",
                height: "auto",
                borderRadius: 1,
                my: 2,
                boxShadow: 1,
              },
            }}
          >
            <Markdown rehypePlugins={[rehypeRaw]} urlTransform={sanitizeUrl}>
              {changelog}
            </Markdown>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseNotesDialog;
